import { useState, useMemo, useCallback } from "react";
import CalculationMethodSelector from "../../../../components/Widgets/Tolerance/CalculationMethodSelector/CalculationMethodSelector.jsx";

import Lengths from "../../../../components/Widgets/Tolerance/Lengths/Lengths";
import ResidualDisbalanceDisplayer from "../../../../components/Widgets/Tolerance/ResidualDisbalanceDisplayer/ResidualDisbalanceDisplayer.jsx";
import PermissibleUnbalancedMassCalculation from "../../../../components/Widgets/Tolerance/Other/PermissibleUnbalancedMassCalculation/PermissibleUnbalancedMassCalculation.jsx";
import CentrifugalForceCalculation from "../../../../components/Widgets/Tolerance/Other/CentrifugalForceCalculation/CentrifugalForceCalculation.jsx";
import {
  CalculationMethods,
  MountTypes,
  RadialSpeed,
  ResidualDisbalance,
  ResidualDisbalanceByPlane,
  SpecificResidualDisbalanceByRadialSpeed,
} from "../../../../utils/helpers/tolerance/GeneralHelper.js";
import { useTranslation } from "react-i18next";
import { Typography, Link } from "@mui/material";
import MountTypeSelector from "../../../../components/Widgets/Tolerance/MountTypeSelector/MountTypeSelector.jsx";
import BalancingTypeHelpContent from "./BalancingTypeHelpContent.jsx";
import multiSupportRotor from "../assets/multi-support-rotor.jpg";
import consoleRotor from "../assets/console-rotor.jpg";

const GeneralTolerance = ({ toleranceClass, mass, isDisabled, radialSpeed, specificResidualDisbalance }) => {
  const { t } = useTranslation();

  const [calculationMethod, setCalculationMethod] = useState(
    CalculationMethods.onePlane
  );
  const [mountType, setMountType] = useState(MountTypes.multiSupport);
  const [lengths, setLengths] = useState({
    length: 0,
    lengthLA: 0,
    lengthLB: 0,
  });

  const [radiusA, setRadiusA] = useState(0);
  const [radiusB, setRadiusB] = useState(0);
  const [radius, setRadius] = useState(0);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const calculationMethods = useMemo(() => {
    return [
      {
        value: CalculationMethods.onePlane,
        label: t(
          "shared.disbalance.components.calculationMethodSelector.methodLabels.onePlane"
        ),
      },
      {
        value: CalculationMethods.dualPlane,
        label: t(
          "shared.disbalance.components.calculationMethodSelector.methodLabels.dualPlane"
        ),
      },
    ];
  }, [t]);

  const isDualPlane = useMemo(
    () => calculationMethod === CalculationMethods.dualPlane,
    [calculationMethod]
  );


  const residualDisbalance = useMemo(
    () =>
      toleranceClass
        ? ResidualDisbalance(toleranceClass.value, mass, radialSpeed) || 0
        : 0,
    [toleranceClass, mass, radialSpeed]
  );

  const residualDisbalancePerPlaneA = useMemo(() => {
    return (
      ResidualDisbalanceByPlane(
        residualDisbalance,
        lengths.length,
        lengths.lengthLB
      ) || 0
    );
  }, [residualDisbalance, lengths]);

  const residualDisbalancePerPlaneB = useMemo(() => {
    return (
      ResidualDisbalanceByPlane(
        residualDisbalance,
        lengths.length,
        lengths.lengthLA
      ) || 0
    );
  }, [residualDisbalance, lengths]);

  const mountTypes = useMemo(() => {
    return [
      {
        image: { alt: "Multi support", src: multiSupportRotor },
        value: MountTypes.multiSupport,
        label: t(
          "shared.disbalance.components.mountTypeSelector.types.multiSupport"
        ),
        help: (
          <>
            <Typography>
              {t(
                "shared.disbalance.components.mountTypeSelector.supportPlanes"
              )}
            </Typography>
            <Typography>
              {t(
                "pages.generalTolerance.components.mountTypeSelector.multiSupportCenterMass"
              )}
            </Typography>
          </>
        ),
      },
      {
        image: { alt: "Console", src: consoleRotor },
        value: MountTypes.console,
        label: t(
          "shared.disbalance.components.mountTypeSelector.types.console"
        ),
        help: (
          <>
            <Typography>
              {t(
                "shared.disbalance.components.mountTypeSelector.supportPlanes"
              )}
            </Typography>
            <Typography>
              {t(
                "pages.generalTolerance.components.mountTypeSelector.consoleCenterMass"
              )}
            </Typography>
          </>
        ),
      },
    ];
  }, [t]);

  const handleModalOpen = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    !isDisabled && (
      <>
        <CalculationMethodSelector
          disabled={isDisabled}
          onChange={setCalculationMethod}
          titleText={t(
            "pages.generalTolerance.components.calculationMethodSelector.title"
          )}
          calculationMethods={calculationMethods}
          defaultCalculationMethod={CalculationMethods.onePlane}
          value={calculationMethod}
          isModalOpen={isModalOpen}
          helpButtonContent={
            <>
              <Typography>
                {t(
                  "pages.generalTolerance.components.calculationMethodSelector.helpButtonContent.diskRotorInfo"
                )}
              </Typography>
              <Link
                component="button"
                variant="body2"
                onClick={handleModalOpen}
              >
                {t(
                  "pages.generalTolerance.components.calculationMethodSelector.helpButtonContent.checkDiskShape"
                )}
              </Link>
            </>
          }
          modalContent={
            <BalancingTypeHelpContent
              onChange={setCalculationMethod}
              onClose={handleModalClose}
              onCloseRequest={handleModalClose}
              open={isModalOpen}
              disabled={!calculationMethod || isDisabled}
            />
          }
        />
        {isDualPlane && (
          <>
            {/* Mount Type */}
            <MountTypeSelector
              title={t(
                "pages.generalTolerance.components.mountTypeSelector.title"
              )}
              mountTypes={mountTypes}
              onChange={setMountType}
              value={mountType}
              defaultMountType={MountTypes.multiSupport}
            />
            {/* Inputs for Dimensions */}
            <Lengths lengths={lengths} onChange={setLengths} />
            <br />
            <br />
          </>
        )}
        {/* Results (Upper A, Upper B, etc.) */}

        <ResidualDisbalanceDisplayer
          title={t("pages.generalTolerance.results.additional")}
          isDualPlane={isDualPlane}
          residualDisbalance={residualDisbalance}
          specificResidualDisbalance={specificResidualDisbalance}
          residualDisbalancePerPlaneA={residualDisbalancePerPlaneA}
          residualDisbalancePerPlaneB={residualDisbalancePerPlaneB}
          mountType={mountType}
          totalDisbalanceHelpButtonText={t(
            "pages.generalTolerance.components.residualDisbalanceDisplayer.components.totalDisbalance.helpButtonText"
          )}
        />
        <Typography variant="h4" sx={{ my: 3 }}>
          {t("shared.disbalance.additionalCalculations")}
        </Typography>
        <PermissibleUnbalancedMassCalculation
          isDualPlane={isDualPlane}
          residualDisbalancePerPlaneA={residualDisbalancePerPlaneA}
          residualDisbalancePerPlaneB={residualDisbalancePerPlaneB}
          residualDisbalance={residualDisbalance}
          setRadiusA={setRadiusA}
          setRadiusB={setRadiusB}
          setRadius={setRadius}
          radialSpeed={radialSpeed}
          radiusA={radiusA}
          radiusB={radiusB}
          radius={radius}
        />
        <CentrifugalForceCalculation
          isDualPlane={isDualPlane}
          mass={mass}
          radiusA={radiusA}
          radiusB={radiusB}
          radius={radius}
          radialSpeed={radialSpeed}
          setRadiusA={setRadiusA}
          setRadiusB={setRadiusB}
          setRadius={setRadius}
        />
      </>
    )
  );
};

export default GeneralTolerance;
